import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Nav, Tab, Alert, Card, Button, Form, Badge, Modal } from 'react-bootstrap';
import { useParams, Navigate, useNavigate } from 'react-router-dom';
import CommunityUsersList from './CommunityUsersList';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigation } from '../../contexts/NavigationContext';
import { getCommunity, updateCommunity } from '../../services/communityService';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faUsers, 
  faMusic, 
  faCog,
  faUserPlus,
  faEdit,
  faSave,
  faTimes,
  faLock,
  faGlobe,
  faPlus,
  faTrash,
  faArrowLeft,
  faCheck,
  faToggleOn,
  faToggleOff
} from "@fortawesome/free-solid-svg-icons";

// Import the event types utility
import {
  ALL_EVENT_TYPES,
  getEventTypeDisplayName
} from '../../utils/EventTypesUtil';
import { useLanguage } from '../../contexts/LanguageContext';

function ManageCommunity() {
  const { communityId } = useParams();
  const [activeTab, setActiveTab] = useState('overview');
  const { user, loading, isAdminOfCommunity, userCommunities, refreshCsrfToken } = useAuth();
  const { isMobile } = useNavigation();
  const { t } = useLanguage();
  const [community, setCommunity] = useState(null);
  const [editingCommunity, setEditingCommunity] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  
  // State for editing community fields
  const [editFormData, setEditFormData] = useState({
    name: '',
    description: '',
    visibility: 'Private',
    supported_event_types: [],
    allow_member_edits: false
  });
  
  // State for managing custom event type
  const [customEventType, setCustomEventType] = useState('');
  const [showEventTypeModal, setShowEventTypeModal] = useState(false);
  
  useEffect(() => {
    async function fetchCommunityDetails() {
      if (!communityId) return;
      
      try {
        setIsLoading(true);
        setError('');
        
        // Using communityService 
        const communityData = await getCommunity(communityId);
        if (communityData) {
          setCommunity(communityData);
          
          // Initialize edit form with community data
          let supportedEventTypes = [];
          try {
            if (communityData.supported_event_types) {
              supportedEventTypes = JSON.parse(communityData.supported_event_types);
            }
          } catch (e) {
          }
          
          setEditFormData({
            name: communityData.name || '',
            description: communityData.description || '',
            visibility: communityData.visibility || 'Private',
            supported_event_types: supportedEventTypes || [],
            allow_member_edits: communityData.allow_member_edits || false
          });
        }
      } catch (error) {
        setError("Napaka pri pridobivanju podatkov o skupnosti");
      } finally {
        setIsLoading(false);
      }
    }
    
    fetchCommunityDetails();
  }, [communityId]);

  // Start editing community
  const handleEditClick = () => {
    setEditingCommunity(true);
  };
  
  // Cancel editing
  const handleCancelEdit = () => {
    let supportedEventTypes = [];
    try {
      if (community.supported_event_types) {
        supportedEventTypes = JSON.parse(community.supported_event_types);
      }
    } catch (e) {
    }
    
    // Reset form data to original values
    setEditFormData({
      name: community.name || '',
      description: community.description || '',
      visibility: community.visibility || 'Private',
      supported_event_types: supportedEventTypes || [],
      allow_member_edits: community.allow_member_edits || false
    });
    
    setEditingCommunity(false);
    setError('');
    setSuccess('');
  };
  
  // Handle form field changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setEditFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  // Handle event type checkbox change
  const handleEventTypeCheckboxChange = (eventType) => {
    setEditFormData(prev => {
      // Check if the event type is already in the array
      const isSelected = prev.supported_event_types.includes(eventType);
      
      if (isSelected) {
        // If it's already selected, remove it
        return {
          ...prev,
          supported_event_types: prev.supported_event_types.filter(type => type !== eventType)
        };
      } else {
        // If it's not selected, add it
        return {
          ...prev,
          supported_event_types: [...prev.supported_event_types, eventType]
        };
      }
    });
  };
  
  // Add custom event type
  const handleAddCustomEventType = () => {
    if (!customEventType.trim()) return;
    
    // Check if the custom type already exists
    if (!editFormData.supported_event_types.includes(customEventType.trim())) {
      setEditFormData(prev => ({
        ...prev,
        supported_event_types: [...prev.supported_event_types, customEventType.trim()]
      }));
    }
    
    setCustomEventType('');
    setShowEventTypeModal(false);
  };
  
  // Remove an event type
  const handleRemoveEventType = (eventType) => {
    setEditFormData(prev => ({
      ...prev,
      supported_event_types: prev.supported_event_types.filter(type => type !== eventType)
    }));
  };
  
  // Save community changes
  const handleSaveCommunity = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      
      // Refresh CSRF token before making request
      await refreshCsrfToken();
      
      // Convert event types array to JSON string for storage
      const updateData = {
        ...editFormData,
        supported_event_types: JSON.stringify(editFormData.supported_event_types)
      };
      
      const response = await updateCommunity(communityId, updateData);
      
      if (response && response.community) {
        setCommunity(response.community);
        setSuccess('Skupina uspešno posodobljena');
        setEditingCommunity(false);
      }
    } catch (error) {
      setError(error.response?.data?.error || 'Napaka pri posodabljanju skupine');
    } finally {
      setSaving(false);
    }
  };

  // Show loading indicator while checking authentication
  if (loading || isLoading) {
    return (
      <div className={isMobile ? "mobile-song-loading" : "container py-5 text-center"}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }
  
  // Check if the user is admin of this community
  const isAdmin = isAdminOfCommunity(parseInt(communityId));

  // Redirect if user is not logged in or not an admin of this community
  if (!user || !isAdmin) {
    return <Navigate to="/" replace />;
  }
  
  // Redirect if community not found
  if (!isLoading && !community) {
    return <Navigate to="/" replace />;
  }

  // Filter out predefined event types from the supported_event_types array to get custom types
  const customEventTypes = editFormData.supported_event_types.filter(
    type => !ALL_EVENT_TYPES.includes(type)
  );

  // MOBILE VIEW
  if (isMobile) {
    return (
      <div className="mobile-song-library">
        <div className="mobile-song-header">
          <div className="mobile-song-header-content">
            <Button 
              variant="link" 
              className="p-0 text-white" 
              onClick={() => navigate(-1)}
              style={{ marginRight: '10px' }}
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </Button>
            <h1 className="text-truncate">{community.name}</h1>
          </div>
        </div>
        
        {error && <Alert variant="danger" className="m-2 p-2">{error}</Alert>}
        {success && <Alert variant="success" className="m-2 p-2">{success}</Alert>}
        
        {/* Mobile tabs */}
        <div className="mobile-song-tabs">
          <button 
            className={`mobile-song-tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            <FontAwesomeIcon icon={faCog} className="me-1" />
            Pregled
          </button>
          <button 
            className={`mobile-song-tab ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            <FontAwesomeIcon icon={faUsers} className="me-1" />
            Uporabniki
          </button>
        </div>
        
        <div className="mobile-song-content">
          {activeTab === 'overview' && (
            <div className="card bg-dark border-0 mb-3">
              <div className="card-header bg-dark border-secondary d-flex justify-content-between align-items-center">
                <div className="fs-6 fw-bold text-white">Informacije o skupini</div>
                {!editingCommunity ? (
                  <button 
                    className="btn btn-outline-primary btn-sm" 
                    onClick={handleEditClick}
                  >
                    <FontAwesomeIcon icon={faEdit} />
                  </button>
                ) : (
                  <div>
                    <button 
                      className="btn btn-outline-danger btn-sm me-1" 
                      onClick={handleCancelEdit}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                    <button 
                      className="btn btn-success btn-sm" 
                      onClick={handleSaveCommunity}
                      disabled={saving}
                    >
                      {saving ? (
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                      ) : (
                        <FontAwesomeIcon icon={faSave} />
                      )}
                    </button>
                  </div>
                )}
              </div>
              <div className="card-body">
                {!editingCommunity ? (
                  // Display mode
                  <div>
                    <div className="mb-3">
                      <div className="fw-bold text-white mb-1">Ime:</div>
                      <div>{community.name}</div>
                    </div>
                    <div className="mb-3">
                      <div className="fw-bold text-white mb-1">Opis:</div>
                      <div>{community.description || 'Ni opisa'}</div>
                    </div>
                    <div className="mb-3">
                      <div className="fw-bold text-white mb-1">Vidnost:</div>
                      <div>
                        {community.visibility === 'Private' ? (
                          <Badge bg="secondary">
                            <FontAwesomeIcon icon={faLock} className="me-1" />
                            Zasebna
                          </Badge>
                        ) : (
                          <Badge bg="info">
                            <FontAwesomeIcon icon={faGlobe} className="me-1" />
                            Javna
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="mb-3">
                      <div className="fw-bold text-white mb-1">Urejanje za člane:</div>
                      <div>
                        {community.allow_member_edits ? (
                          <Badge bg="success">Omogočeno</Badge>
                        ) : (
                          <Badge bg="secondary">Onemogočeno</Badge>
                        )}
                      </div>
                    </div>
                    <div className="mb-3">
                      <div className="fw-bold text-white mb-1">Vrste dogodkov:</div>
                      <div>
                        {community.supported_event_types ? (
                          <div className="d-flex flex-wrap gap-1">
                            {(() => {
                              try {
                                const eventTypes = JSON.parse(community.supported_event_types);
                                return eventTypes.length > 0 ? (
                                  eventTypes.map((type, index) => (
                                    <Badge key={index} bg="light" text="dark" className="mb-1">
                                      {getEventTypeDisplayName(type, t)}
                                    </Badge>
                                  ))
                                ) : 'Ni definiranih vrst dogodkov';
                              } catch (e) {
                                return 'Ni definiranih vrst dogodkov';
                              }
                            })()}
                          </div>
                        ) : (
                          'Ni definiranih vrst dogodkov'
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  // Edit form optimized for mobile
                  <Form>
                    <Form.Group className="mb-3">
                      <Form.Label className="text-white">Ime skupine</Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        value={editFormData.name}
                        onChange={handleInputChange}
                        required
                        className="bg-dark text-white border-secondary"
                      />
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                      <Form.Label className="text-white">Opis</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="description"
                        value={editFormData.description}
                        onChange={handleInputChange}
                        className="bg-dark text-white border-secondary"
                      />
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                      <Form.Label className="text-white">Vidnost</Form.Label>
                      <Form.Select
                        name="visibility"
                        value={editFormData.visibility}
                        onChange={handleInputChange}
                        className="bg-dark text-white border-secondary"
                      >
                        <option value="Private">Zasebna - Vidna samo članom</option>
                        <option value="Public">Javna - Vidna vsem</option>
                      </Form.Select>
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                      <div className="d-flex align-items-center mb-2">
                        <Form.Check
                          type="checkbox"
                          name="allow_member_edits"
                          checked={editFormData.allow_member_edits}
                          onChange={handleInputChange}
                          className="me-2"
                        />
                        <Form.Label className="text-white mb-0">
                          Dovoli članom urejanje vsebin
                        </Form.Label>
                      </div>
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                      <Form.Label className="text-white">Vrste dogodkov</Form.Label>
                      <div className="bg-dark p-2 border border-secondary rounded mb-2">
                        <div className="d-flex flex-wrap gap-2">
                          {ALL_EVENT_TYPES.map((eventType) => (
                            <div key={eventType} 
                              className={`badge ${editFormData.supported_event_types.includes(eventType) ? 'bg-primary' : 'bg-secondary'}`}
                              onClick={() => handleEventTypeCheckboxChange(eventType)}
                              style={{ cursor: 'pointer' }}
                            >
                              {getEventTypeDisplayName(eventType, t)}
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      {/* Custom event types */}
                      {customEventTypes.length > 0 && (
                        <div className="mb-2">
                          <div className="small text-white fw-bold mb-1">Dodatne vrste:</div>
                          <div className="d-flex flex-wrap gap-1">
                            {customEventTypes.map((type, index) => (
                              <Badge 
                                key={index} 
                                bg="light" 
                                text="dark" 
                                className="p-2"
                              >
                                {type}
                                <Button
                                  variant="link"
                                  className="p-0 ms-1 text-danger"
                                  onClick={() => handleRemoveEventType(type)}
                                  style={{ fontSize: '0.8rem' }}
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                </Button>
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      <Button 
                        variant="outline-primary" 
                        size="sm"
                        onClick={() => setShowEventTypeModal(true)}
                        className="w-100"
                      >
                        <FontAwesomeIcon icon={faPlus} className="me-1" />
                        Dodaj vrsto dogodka
                      </Button>
                    </Form.Group>
                  </Form>
                )}
              </div>
            </div>
          )}
          
          {activeTab === 'users' && (
            <CommunityUsersList communityId={communityId} />
          )}
        </div>
        
        {/* Modal for adding custom event type */}
        <Modal 
          show={showEventTypeModal} 
          onHide={() => setShowEventTypeModal(false)}
          contentClassName="modal-container"
        >
          <Modal.Header className="modal-header">
            <Modal.Title>Dodaj vrsto dogodka</Modal.Title>
            <Button 
              variant="link" 
              onClick={() => setShowEventTypeModal(false)}
              className="btn-close"
            />
          </Modal.Header>
          <Modal.Body className="modal-body">
            <Form.Group>
              <Form.Label>Naziv vrste dogodka</Form.Label>
              <Form.Control
                type="text"
                value={customEventType}
                onChange={(e) => setCustomEventType(e.target.value)}
                placeholder="Vnesite ime za dodatno vrsto dogodka"
                className="form-control"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className="modal-footer">
            <Button variant="secondary" onClick={() => setShowEventTypeModal(false)}>
              Prekliči
            </Button>
            <Button variant="primary" onClick={handleAddCustomEventType}>
              Dodaj
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }

  // DESKTOP VIEW
  return (
    <Container className="py-4">
      <h2 className="mb-4">Upravljaj skupino: {community.name}</h2>
      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}
      
      <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
        <Row>
          <Col md={3}>
            <div className="card mb-4">
              <div className="card-header">Navigacija</div>
              <Nav variant="pills" className="flex-column">
                <Nav.Item>
                  <Nav.Link 
                    eventKey="overview" 
                    className="rounded-0 border-0"
                  >
                    <FontAwesomeIcon icon={faCog} className="me-2" />
                    Pregled
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link 
                    eventKey="users" 
                    className="rounded-0 border-0"
                  >
                    <FontAwesomeIcon icon={faUsers} className="me-2" />
                    Uporabniki
                  </Nav.Link>
                </Nav.Item>
              </Nav>
            </div>
          </Col>
          
          <Col md={9}>
            <Tab.Content>
              <Tab.Pane eventKey="overview">
                <Card className="shadow-sm">
                  <Card.Header className="bg-transparent">
                    <div className="d-flex justify-content-between align-items-center">
                      <h5 className="mb-0">Informacije o skupini</h5>
                      {!editingCommunity ? (
                        <Button size="sm" variant="outline-primary" onClick={handleEditClick}>
                          <FontAwesomeIcon icon={faEdit} className="me-1" />
                          Uredi
                        </Button>
                      ) : (
                        <div>
                          <Button size="sm" variant="outline-danger" className="me-2" onClick={handleCancelEdit}>
                            <FontAwesomeIcon icon={faTimes} className="me-1" />
                            Prekliči
                          </Button>
                          <Button 
                            size="sm" 
                            variant="success" 
                            onClick={handleSaveCommunity}
                            disabled={saving}
                          >
                            {saving ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                Shranjevanje...
                              </>
                            ) : (
                              <>
                                <FontAwesomeIcon icon={faSave} className="me-1" />
                                Shrani
                              </>
                            )}
                          </Button>
                        </div>
                      )}
                    </div>
                  </Card.Header>
                  <Card.Body>
                    {!editingCommunity ? (
                      // Display mode - desktop
                      <>
                        <Row className="mb-3">
                          <Col md={3} className="fw-bold">Ime:</Col>
                          <Col md={9}>{community.name}</Col>
                        </Row>
                        <Row className="mb-3">
                          <Col md={3} className="fw-bold">Opis:</Col>
                          <Col md={9}>{community.description || 'Ni opisa'}</Col>
                        </Row>
                        <Row className="mb-3">
                          <Col md={3} className="fw-bold">Vidnost:</Col>
                          <Col md={9}>
                            {community.visibility === 'Private' ? (
                              <Badge bg="secondary">
                                <FontAwesomeIcon icon={faLock} className="me-1" />
                                Zasebna
                              </Badge>
                            ) : (
                              <Badge bg="info">
                                <FontAwesomeIcon icon={faGlobe} className="me-1" />
                                Javna
                              </Badge>
                            )}
                            {community.visibility === '' && (
                              <small className="ms-2 text-muted">
                                (Prazna vrednost - obravnavano kot javno)
                              </small>
                            )}
                          </Col>
                        </Row>
                        <Row className="mb-3">
                          <Col md={3} className="fw-bold">Urejanje za člane:</Col>
                          <Col md={9}>
                            {community.allow_member_edits ? (
                              <Badge bg="success">Omogočeno</Badge>
                            ) : (
                              <Badge bg="secondary">Onemogočeno</Badge>
                            )}
                          </Col>
                        </Row>
                        <Row className="mb-3">
                          <Col md={3} className="fw-bold">Vrste dogodkov:</Col>
                          <Col md={9}>
                            {community.supported_event_types ? (
                              <div>
                                {(() => {
                                  try {
                                    const eventTypes = JSON.parse(community.supported_event_types);
                                    return eventTypes.length > 0 ? (
                                      eventTypes.map((type, index) => (
                                        <Badge key={index} bg="light" text="dark" className="me-1 mb-1">
                                          {getEventTypeDisplayName(type, t)}
                                        </Badge>
                                      ))
                                    ) : 'Ni definiranih vrst dogodkov';
                                  } catch (e) {
                                    return 'Ni definiranih vrst dogodkov';
                                  }
                                })()}
                              </div>
                            ) : (
                              'Ni definiranih vrst dogodkov'
                            )}
                          </Col>
                        </Row>
                      </>
                    ) : (
                      // Edit mode - desktop
                      <Form>
                        <Form.Group className="mb-3">
                          <Form.Label>Ime skupine</Form.Label>
                          <Form.Control
                            type="text"
                            name="name"
                            value={editFormData.name}
                            onChange={handleInputChange}
                            required
                          />
                        </Form.Group>
                        
                        <Form.Group className="mb-3">
                          <Form.Label>Opis</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            name="description"
                            value={editFormData.description}
                            onChange={handleInputChange}
                          />
                        </Form.Group>
                        
                        <Form.Group className="mb-3">
                          <Form.Label>Vidnost</Form.Label>
                          <Form.Select
                            name="visibility"
                            value={editFormData.visibility}
                            onChange={handleInputChange}
                          >
                            <option value="Private">Zasebna - Vidna samo članom</option>
                            <option value="Public">Javna - Vidna vsem</option>
                          </Form.Select>
                          <Form.Text className="text-muted">
                            Zasebne skupine so vidne samo članom in globalnim administratorjem.
                          </Form.Text>
                        </Form.Group>
                        
                        <Form.Group className="mb-3">
                          <Form.Check
                            type="checkbox"
                            label="Dovoli članom urejanje vsebin (dogodki, setlisti, pesmi)"
                            name="allow_member_edits"
                            checked={editFormData.allow_member_edits}
                            onChange={handleInputChange}
                          />
                          <Form.Text className="text-muted">
                            Če je omogočeno, lahko vsi člani skupine urejajo vsebine. V nasprotnem primeru lahko vsebine urejajo samo administratorji.
                          </Form.Text>
                        </Form.Group>
                        
                        <Form.Group className="mb-4">
                          <Form.Label>Podprte vrste dogodkov</Form.Label>
                          
                          {/* Checkbox list for predefined event types */}
                          <Card className="mb-3">
                            <Card.Body>
                              <div className="d-flex flex-wrap">
                                {ALL_EVENT_TYPES.map((eventType) => (
                                  <Form.Check 
                                    key={eventType}
                                    type="checkbox"
                                    id={`event-type-${eventType}`}
                                    label={getEventTypeDisplayName(eventType, t)}
                                    checked={editFormData.supported_event_types.includes(eventType)}
                                    onChange={() => handleEventTypeCheckboxChange(eventType)}
                                    className="me-3 mb-2"
                                  />
                                ))}
                              </div>
                            </Card.Body>
                          </Card>
                          
                          {/* Display custom event types with delete option */}
                          {customEventTypes.length > 0 && (
                            <div className="mb-3">
                              <Form.Label>Dodatne vrste dogodkov:</Form.Label>
                              <div>
                                {customEventTypes.map((type, index) => (
                                  <Badge 
                                    key={index} 
                                    bg="light" 
                                    text="dark" 
                                    className="me-1 mb-1 p-2"
                                  >
                                    {type}
                                    <Button
                                      variant="link"
                                      className="p-0 ms-2 text-danger"
                                      onClick={() => handleRemoveEventType(type)}
                                      style={{ fontSize: '0.8rem' }}
                                    >
                                      <FontAwesomeIcon icon={faTrash} />
                                    </Button>
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          <Button 
                            variant="outline-primary" 
                            size="sm"
                            onClick={() => setShowEventTypeModal(true)}
                          >
                            <FontAwesomeIcon icon={faPlus} className="me-1" />
                            Dodaj dodatno vrsto dogodka
                          </Button>
                        </Form.Group>
                      </Form>
                    )}
                  </Card.Body>
                </Card>
              </Tab.Pane>
              
              <Tab.Pane eventKey="users">
                <CommunityUsersList communityId={communityId} />
              </Tab.Pane>
            </Tab.Content>
          </Col>
        </Row>
      </Tab.Container>
      
      {/* Modal for adding custom event type */}
      <Modal show={showEventTypeModal} onHide={() => setShowEventTypeModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Dodaj dodatno vrsto dogodka</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Naziv vrste dogodka</Form.Label>
            <Form.Control
              type="text"
              value={customEventType}
              onChange={(e) => setCustomEventType(e.target.value)}
              placeholder="Vnesite ime za dodatno vrsto dogodka"
            />
            <Form.Text className="text-muted">
              Za običajne vrste dogodkov (vaje, maše, koncerte, itd.) uporabite zgornje možnosti.
              To polje uporabite le za specifične vrste dogodkov, ki niso na seznamu.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEventTypeModal(false)}>
            Prekliči
          </Button>
          <Button variant="primary" onClick={handleAddCustomEventType}>
            Dodaj
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default ManageCommunity;